import {
  getLocalTodayDateOnly,
  parseDateOnlyLocal,
} from "../utils/dateFormatting";

// Legacy interfaces for backward compatibility
export interface OtherGuest {
  guestId: string;
  name: string;
}

export interface Charge {
  chargeId: string;
  description: string;
  amount: number;
  date: string; // ISO date string
  requestId?: string; // Optional requestId to link charges to requests
}

export interface RoomInfo {
  roomId: string;
  roomNumber: string;
  floor: string | number;
  type: string;
}

export interface RequestHistoryItem {
  requestId: string;
  requestType: string;
  submittedAt: string; // ISO date string
  status: string;
}

// Dietary restriction interface
export interface DietaryRestriction {
  dietaryRestrictionId: string;
  code: string;
  name: string;
  description: string | null;
}

// Legacy interface - keeping for backward compatibility with existing components
export interface GuestProfile {
  guestId: string;
  title: string | null;
  name: string;
  dateOfBirth: string | null;
  gender: string | null;
  checkInDate: string; // ISO date string
  checkOutDate?: string; // ISO date string (optional for current/future)
  // Use flexible status to reflect backend values (e.g., RoomReserved, CheckedIn, CheckedOut, Cancelled, etc.)
  status: string;
  otherGuests: OtherGuest[];
  recentCharges: Charge[];
  totalBill: number;
  preferences: string[]; // merged view for backwards-compat
  // Structured preference fields
  preferencesGeneral?: string[]; // General Stay preferences
  preferencesHotelInterests?: string[]; // Hotel Interests
  preferencesAccessibilityNeeds?: string[]; // Accessibility needs
  preferencesCommunication?: string[]; // Communication preferences
  preferencesDietaryRestrictions?: DietaryRestriction[]; // Dietary restrictions
  room: RoomInfo;
  requestHistory: RequestHistoryItem[];
  email?: string;
  phone?: string;
}

const TERMINAL_STAY_STATUSES = new Set([
  "checkedout",
  "cancelled",
  "canceled",
  "noshow",
  "no-show",
  "departed",
]);

function normalizeStayStatus(status: string | null | undefined): string {
  return (status || "").toLowerCase().replace(/\s+/g, "");
}

function isTerminalStayStatus(status: string | null | undefined): boolean {
  return TERMINAL_STAY_STATUSES.has(normalizeStayStatus(status));
}

/**
 * Derive a display status from estimated stay dates when the backend has no
 * currentStay (common for scrape-only properties without check-in webhooks).
 * Checkout day is still treated as in-house until the following calendar day.
 */
export function deriveStatusFromStayDates(
  checkIn: string | null | undefined,
  checkOut: string | null | undefined,
  fallbackStatus?: string | null
): string {
  // Normalize to zero-padded YYYY-MM-DD so string comparison is reliable
  // regardless of the raw date format the backend/scraper emits.
  const toDayKey = (value: string | null | undefined): string | null => {
    const parsed = parseDateOnlyLocal(value);
    if (!parsed) return null;
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const today = getLocalTodayDateOnly();
  const checkInDay = toDayKey(checkIn);
  const checkOutDay = toDayKey(checkOut);

  if (checkOutDay && today > checkOutDay) {
    return "CheckedOut";
  }
  if (checkInDay && today < checkInDay) {
    return fallbackStatus || "FutureBooking";
  }
  if (checkInDay && (!checkOutDay || today <= checkOutDay)) {
    return fallbackStatus || "CheckedIn";
  }

  return fallbackStatus || "CheckedOut";
}

interface DetailCurrentStay {
  status?: string | null;
  checkIn?: { actual?: string | null; estimated?: string | null } | null;
  checkOut?: { actual?: string | null; estimated?: string | null } | null;
}

interface DetailRecentStay {
  status?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
}

function resolveGuestDetailStatus(
  currentStay: DetailCurrentStay | null | undefined,
  mostRecentStay: DetailRecentStay | null | undefined
): string {
  if (currentStay?.status) {
    return currentStay.status;
  }

  if (currentStay) {
    if (currentStay.checkIn?.actual && !currentStay.checkOut?.actual) {
      return "CheckedIn";
    }
    // Only derive from estimates when there is no actual checkout either;
    // an actual checkout means the stay has ended regardless of estimates.
    if (!currentStay.checkIn?.actual && !currentStay.checkOut?.actual) {
      // No actual check-in: use estimated dates so mid-stay scrape guests
      // are not labeled FutureBooking / CheckedOut incorrectly.
      return deriveStatusFromStayDates(
        currentStay.checkIn?.estimated,
        currentStay.checkOut?.estimated,
        currentStay.status
      );
    }
    return "CheckedOut";
  }

  // Scrape-only stays often land only in recentStays (no actual check-in →
  // backend currentStay is null). Prefer backend status when terminal;
  // otherwise derive from estimated dates so active stays aren't "Checked Out".
  if (mostRecentStay) {
    const recentStatus = mostRecentStay.status;
    if (recentStatus && isTerminalStayStatus(recentStatus)) {
      return recentStatus;
    }
    return deriveStatusFromStayDates(
      mostRecentStay.checkIn,
      mostRecentStay.checkOut,
      recentStatus
    );
  }

  return "CheckedOut";
}

// Helper function to transform backend data to legacy format for existing components
export const transformGuestDetailToLegacyProfile = (
  guestDetail: any
): GuestProfile => {
  const currentStay = guestDetail.stayInformation?.currentStay;
  const mostRecentStay = guestDetail.stayInformation?.recentStays?.[0];

  const status = resolveGuestDetailStatus(currentStay, mostRecentStay);

  return {
    guestId: guestDetail.guestId || "",
    title: guestDetail.identity?.name?.title || null,
    name: guestDetail.identity?.name?.fullName || "",
    dateOfBirth: guestDetail.identity?.dateOfBirth || null,
    gender: guestDetail.identity?.gender || null,
    checkInDate:
      currentStay?.checkIn?.actual ||
      currentStay?.checkIn?.estimated ||
      mostRecentStay?.checkIn ||
      "",
    checkOutDate:
      currentStay?.checkOut?.actual ||
      currentStay?.checkOut?.estimated ||
      mostRecentStay?.checkOut,
    status,
    otherGuests: currentStay?.otherGuestsInBooking || [],
    recentCharges: (guestDetail.billing?.allCharges || []).map(
      (charge: any) => {
        // Match charge to request by date - find the dining request created around the same time
        const chargeDate = new Date(charge.chargeDate);
        const matchingRequest = (guestDetail.requestHistory || []).find(
          (request: any) => {
            if (request.type !== "Dining") return false;
            const requestDate = new Date(request.createdAt);
            // Match if within 1 minute of each other
            const timeDiff = Math.abs(
              chargeDate.getTime() - requestDate.getTime()
            );
            return timeDiff < 60000; // 1 minute
          }
        );

        return {
          chargeId: charge.chargeId,
          description: charge.description.replace(
            /Dining Order Item/i,
            "Dining Order"
          ),
          amount: charge.amount,
          date: charge.chargeDate,
          requestId: matchingRequest?.requestId,
        };
      }
    ),
    totalBill: guestDetail.billing?.totalBill || 0,
    // Preferences - map from new API structure
    preferencesGeneral: Array.isArray(
      guestDetail.preferences?.generalStayPreferences
    )
      ? guestDetail.preferences.generalStayPreferences
      : [],
    preferencesHotelInterests: Array.isArray(
      guestDetail.profileInfo?.hotelSpecificPreferences?.interests
    )
      ? guestDetail.profileInfo.hotelSpecificPreferences.interests
      : [],
    preferencesAccessibilityNeeds: Array.isArray(
      guestDetail.preferences?.globalAccessibilityNeeds
    )
      ? guestDetail.preferences.globalAccessibilityNeeds
      : [],
    preferencesCommunication: Array.isArray(
      guestDetail.preferences?.globalCommunicationPreferences
    )
      ? guestDetail.preferences.globalCommunicationPreferences
      : [],
    preferencesDietaryRestrictions: Array.isArray(
      guestDetail.preferences?.dietaryRestrictions
    )
      ? guestDetail.preferences.dietaryRestrictions
      : [],
    preferences: [
      ...(Array.isArray(guestDetail.preferences?.generalStayPreferences)
        ? guestDetail.preferences.generalStayPreferences
        : []),
      ...(Array.isArray(
        guestDetail.profileInfo?.hotelSpecificPreferences?.interests
      )
        ? guestDetail.profileInfo.hotelSpecificPreferences.interests
        : []),
      ...(Array.isArray(guestDetail.preferences?.globalAccessibilityNeeds)
        ? guestDetail.preferences.globalAccessibilityNeeds
        : []),
      ...(Array.isArray(guestDetail.preferences?.globalCommunicationPreferences)
        ? guestDetail.preferences.globalCommunicationPreferences
        : []),
      ...(Array.isArray(guestDetail.preferences?.dietaryRestrictions)
        ? guestDetail.preferences.dietaryRestrictions.map((d: any) => d.name)
        : []),
    ],
    room:
      currentStay?.room || mostRecentStay?.room
        ? {
            roomId:
              currentStay?.room?.roomNumber ||
              mostRecentStay?.room?.roomNumber ||
              "unknown",
            roomNumber:
              currentStay?.room?.roomNumber ||
              mostRecentStay?.room?.roomNumber ||
              "N/A",
            floor: currentStay?.room?.floor || mostRecentStay?.room?.floor || 1,
            type: "Standard", // Default type since not provided by backend
          }
        : {
            roomId: "unknown",
            roomNumber: "N/A",
            floor: 0,
            type: "N/A",
          },
    requestHistory: (guestDetail.requestHistory || []).map((request: any) => ({
      requestId: request.requestId,
      requestType: request.type,
      submittedAt: request.createdAt,
      status: request.status,
    })),
    email: guestDetail.identity?.contact?.primaryEmail,
    phone: guestDetail.identity?.contact?.primaryPhone,
  };
};

// Helper function to transform directory item to legacy profile format
export const transformDirectoryItemToLegacyProfile = (
  directoryItem: any
): GuestProfile => {
  const currentStay = directoryItem.currentStay;

  // Prefer backend status; when missing, derive from stay dates so scrape-only
  // guests mid-stay are not defaulted to CheckedOut.
  const status: string = currentStay?.status
    ? currentStay.status
    : currentStay
      ? deriveStatusFromStayDates(currentStay.checkIn, currentStay.checkOut)
      : "CheckedOut";

  return {
    guestId: directoryItem.guestId,
    title: directoryItem.name.title || null,
    name: directoryItem.name.fullName,
    dateOfBirth: directoryItem.dateOfBirth || null,
    gender: directoryItem.gender || null,
    checkInDate: currentStay?.checkIn || "",
    checkOutDate: currentStay?.checkOut,
    status,
    otherGuests: [], // Not available in directory view
    recentCharges: [], // Not available in directory view
    totalBill: 0, // Not available in directory view
    preferences: [], // Not available in directory view
    room: currentStay?.room
      ? {
          roomId: currentStay.room.roomNumber,
          roomNumber: currentStay.room.roomNumber,
          floor: 1, // Default floor
          type: "Standard", // Default type
        }
      : {
          roomId: "unknown",
          roomNumber: "N/A",
          floor: 0,
          type: "N/A",
        },
    requestHistory: [], // Not available in directory view
    email: directoryItem.contact.email,
    phone: directoryItem.contact.phone,
  };
};
